import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import tWithDefault from "parog-web/utils/t-with-default";
//---- Automatically generated, don't update this part ----

export default class HemodialysisSessionDeclaredJournalEvent extends JournalEvent {
  static type = "hemodialysis-session-declared-journal-event";

  /**
   * Real weight start of the session
   */
  @attr("number") declare startRealWeight: number;

  /**
   * Dry weight start of the session
   */
  @attr("number") declare startDryWeight: number;

  /**
   * Weight loss middle of the session
   */
  @attr("number") declare middleWeightLoss: number;

  /**
   * Output weight end of the session
   */
  @attr("number") declare endOutputWeight: number;

  /**
   * Dry weight variation end of the session
   */
  @attr("number") declare endDryWeightVariation: number;

  /**
   * Arterial systolic pressure start of the session
   */
  @attr("number") declare startArterialSystolicPressure: number;

  /**
   * Arterial diastolic pressure start of the session
   */
  @attr("number") declare startArterialDiastolicPressure: number;

  /**
   * Heart rate start of the session
   */
  @attr("number") declare startHeartRate: number;

  /**
   * Arterial systolic pressure middle of the session
   */
  @attr("number") declare middleArterialSystolicPressure: number;

  /**
   * Arterial diastolic pressure middle of the session
   */
  @attr("number") declare middleArterialDiastolicPressure: number;

  /**
   * Heart rate middle of the session
   */
  @attr("number") declare middleHeartRate: number;

  /**
   * Arterial systolic pressure end of the session
   */
  @attr("number") declare endArterialSystolicPressure: number;

  /**
   * Arterial diastolic pressure end of the session
   */
  @attr("number") declare endArterialDiastolicPressure: number;

  /**
   * Heart rate end of the session
   */
  @attr("number") declare endHeartRate: number;

  /**
   * Ultrafiltration rate middle of the session
   */
  @attr("number") declare middleUltrafiltrationRate: number;

  /**
   * Machine arterial systolic pressure middle of the session
   */
  @attr("number") declare middleMachineArterialSystolicPressure: number;

  /**
   * Machine arterial diastolic pressure middle of the session
   */
  @attr("number") declare middleMachineArterialDiastolicPressure: number;

  /**
   * Machine venous pressure middle of the session
   */
  @attr("number") declare middleMachineVenousPressure: number;

  /**
   * Weight compression duration end of the session
   */
  @attr("number") declare endWeightCompressionDuration: number;

  static validations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.hemodialysis-session-declared-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      startRealWeight: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.hemodialysis-session-declared-journal-event.start-real-weight"
        )
      }),
      startDryWeight: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.hemodialysis-session-declared-journal-event.start-dry-weight"
        )
      }),
      middleWeightLoss: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.hemodialysis-session-declared-journal-event.middle-weight-loss"
        )
      }),
      endOutputWeight: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.hemodialysis-session-declared-journal-event.end-output-weight"
        )
      }),
      endDryWeightVariation: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.hemodialysis-session-declared-journal-event.end-dry-weight-variation"
        )
      }),
      startArterialSystolicPressure: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.hemodialysis-session-declared-journal-event.start-arterial-systolic-pressure"
        )
      }),
      startArterialDiastolicPressure: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.hemodialysis-session-declared-journal-event.start-arterial-diastolic-pressure"
        )
      }),
      startHeartRate: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.hemodialysis-session-declared-journal-event.start-heart-rate"
        )
      }),
      middleArterialSystolicPressure: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.hemodialysis-session-declared-journal-event.middle-arterial-systolic-pressure"
        )
      }),
      middleArterialDiastolicPressure: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.hemodialysis-session-declared-journal-event.middle-arterial-diastolic-pressure"
        )
      }),
      middleHeartRate: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.hemodialysis-session-declared-journal-event.middle-heart-rate"
        )
      }),
      endArterialSystolicPressure: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.hemodialysis-session-declared-journal-event.end-arterial-systolic-pressure"
        )
      }),
      endArterialDiastolicPressure: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.hemodialysis-session-declared-journal-event.end-arterial-diastolic-pressure"
        )
      }),
      endHeartRate: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.hemodialysis-session-declared-journal-event.end-heart-rate"
        )
      }),
      middleUltrafiltrationRate: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.hemodialysis-session-declared-journal-event.middle-ultrafiltration-rate"
        )
      }),
      middleMachineArterialSystolicPressure: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.hemodialysis-session-declared-journal-event.middle-machine-arterial-systolic-pressure"
        )
      }),
      middleMachineArterialDiastolicPressure: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.hemodialysis-session-declared-journal-event.middle-machine-arterial-diastolic-pressure"
        )
      }),
      middleMachineVenousPressure: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.hemodialysis-session-declared-journal-event.middle-machine-venous-pressure"
        )
      }),
      endWeightCompressionDuration: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.hemodialysis-session-declared-journal-event.end-weight-compression-duration"
        )
      })
    };
  }

  //---- End of generated code ----
  @belongsTo("patient", {
    async: false,
    inverse: "hemodialysisSessionDeclaredJournalEvents"
  })
  declare patient: Patient;
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "hemodialysis-session-declared-journal-event": HemodialysisSessionDeclaredJournalEvent;
  }
}
